const validVersion = (value) =>
  typeof value === 'string' && /^[a-zA-Z0-9._-]{1,128}$/.test(value);

// Check the actual page and its bundles: version.json can precede asset rollout.
export async function isAppUpdateReady({ windowObject, fetchClient, signal }) {
  const { document, DOMParser: Parser, location } = windowObject;
  if (!document || !Parser || !location?.href) return false;
  const scripts = (doc) =>
    Array.from(doc.querySelectorAll('script[src]'))
      .map((node) => new URL(node.getAttribute('src'), location.href).href)
      .filter((url) => new URL(url).pathname.startsWith('/packs/'))
      .sort();
  const loaded = scripts(document);
  const url = new URL(location.href);
  url.searchParams.set('_otello_update_check', Date.now());
  const options = { cache: 'no-store', signal };
  const page = await fetchClient(url.href, options);
  if (!page.ok || page.redirected) return false;
  const nextDocument = new Parser().parseFromString(
    await page.text(),
    'text/html'
  );
  const next = scripts(nextDocument);
  if (
    !loaded.length ||
    !next.length ||
    JSON.stringify(loaded) === JSON.stringify(next)
  ) {
    return false;
  }
  const assets = await Promise.all(
    next.map(async (asset) => {
      const result = await fetchClient(asset, options);
      if (
        !result.ok ||
        result.redirected ||
        !/javascript/.test(result.headers.get('content-type') || '')
      )
        return false;
      // Consume the body so a truncated or incomplete download cannot enable reload.
      return (await result.text()).length > 0;
    })
  );
  return assets.every(Boolean);
}

// Compare with the code loaded by this tab, never another tab's localStorage.
export function startAppUpdateMonitor({
  buildVersion,
  onUpdate,
  windowObject = typeof window !== 'undefined' ? window : null,
  fetchClient = typeof fetch !== 'undefined' ? fetch : null,
  intervalMs = 60000,
  checkReady = isAppUpdateReady,
} = {}) {
  if (!windowObject || !fetchClient || !validVersion(buildVersion)) {
    return () => {};
  }

  let stopped = false;
  let checking = false;
  let available = false;
  let controller;
  let timeout;

  const check = async () => {
    if (stopped || checking) return false;
    checking = true;
    controller = new AbortController();
    timeout = windowObject.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetchClient(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Version unavailable');
      const { version } = await response.json();
      if (stopped) return false;
      if (!validVersion(version)) throw new Error('Invalid version');
      const next =
        version !== buildVersion &&
        (await checkReady({
          windowObject,
          fetchClient,
          signal: controller.signal,
        }));
      if (stopped) return false;
      if (next !== available) {
        available = next;
        onUpdate(next);
      }
      return next;
    } catch (_) {
      // Offline or unavailable version endpoint must never interrupt the app.
      if (!stopped && available) {
        available = false;
        onUpdate(false);
      }
      return false;
    } finally {
      windowObject.clearTimeout(timeout);
      checking = false;
    }
  };

  windowObject.addEventListener('focus', check);
  const interval = windowObject.setInterval(check, intervalMs);
  check();

  const stop = () => {
    stopped = true;
    windowObject.removeEventListener('focus', check);
    windowObject.clearInterval(interval);
    windowObject.clearTimeout(timeout);
    controller?.abort();
  };
  stop.check = check;
  return stop;
}
